import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { useSiteContent } from "@/lib/hooks";

export default function Footer() {
  const { data: c } = useSiteContent();
  const social = c?.social_links || {};
  return (
    <footer className="bg-slate-900 text-slate-300 mt-auto" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="inline-flex bg-white rounded-xl p-3 mb-4">
            <img src="/logo.png" alt={c?.company_name || "Speedex International"} className="h-12 w-auto" />
          </div>
          <p className="text-sm text-slate-400 max-w-md leading-relaxed">{c?.strapline}</p>
          <div className="flex gap-3 mt-5">
            {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-slate-800 grid place-items-center hover:bg-green-600 transition-colors" data-testid="footer-facebook">
                <Facebook className="h-4 w-4" />
              </a>
            )}
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-slate-800 grid place-items-center hover:bg-green-600 transition-colors" data-testid="footer-instagram">
                <Instagram className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Explore</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/services" className="hover:text-green-400">Services</Link></li>
            <li><Link to="/gallery" className="hover:text-green-400">Gallery</Link></li>
            <li><Link to="/quote" className="hover:text-green-400">Get a Quote</Link></li>
            <li><Link to="/about" className="hover:text-green-400">About & Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contact</h4>
          <ul className="space-y-3 text-sm">
            {c?.contact_phone && <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-green-500" /><a href={`tel:${c.contact_phone}`} className="hover:text-green-400">{c.contact_phone}</a></li>}
            {c?.contact_email && <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-green-500" /><a href={`mailto:${c.contact_email}`} className="hover:text-green-400 break-all">{c.contact_email}</a></li>}
            {c?.contact_address && <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-green-500" /><span>{c.contact_address}</span></li>}
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="text-center sm:text-left space-y-1">
            <p>© {new Date().getFullYear()} {c?.company_name || "Speedex International"}. All rights reserved.</p>
            {(c?.company_number || c?.registered_office) && (
              <p data-testid="footer-legal-details">
                {c?.company_number && <>Registered in England & Wales, company no. {c.company_number}. </>}
                {c?.registered_office && <>Registered office: {c.registered_office}</>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-slate-300" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/cookies" className="hover:text-slate-300" data-testid="footer-cookies-link">Cookies</Link>
            <Link to="/terms" className="hover:text-slate-300" data-testid="footer-terms-link">Terms</Link>
            <Link to="/admin/login" className="text-[10px] text-slate-600 hover:text-slate-400 opacity-60 transition-opacity" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
